import { Component, Inject, OnInit, viewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatDialogClose } from '@angular/material/dialog';
import { ErrorHandler } from '@app/core/services/error-handler.service';
import { ToastService } from '@app/core/services/toast.service';
import { FeedbackComponent } from '@app/shared/components/feedback/feedback.component';
import { SignatureFieldComponent } from '@app/shared/components/signature-field/signature-field.component';
import {
    isValidSignatureDataUrl,
    signatureRequiredValidator,
} from '@app/shared/signature-utils';
import { TokensExternalSignaturePad } from '@app/shared/models/business-model/signingPadTokens';
import { environment } from '@env/environment';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { DeliveryNoteBundlesService, SignaturePadsService } from 'api/services';
import { base64StringToBlob, blobToDataURL } from 'blob-util';
import { NgxSpinnerService } from 'ngx-spinner';
import { lastValueFrom } from 'rxjs';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { QrCodeComponent } from 'ng-qrcode';
import { SignatureFieldComponent as SignatureFieldComponent_1 } from '../../../shared/components/signature-field/signature-field.component';
import { MatTooltip } from '@angular/material/tooltip';

export interface IncomingData {
    organizationSiteKey: string;
    deliveryNoteBundleKeys: string[];
}

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-checkout-modal-dialog',
    templateUrl: './checkout-modal-dialog.component.html',
    styleUrls: ['./checkout-modal-dialog.component.scss'],
    imports: [
        MatIconButton,
        MatDialogClose,
        MatIcon,
        QrCodeComponent,
        ReactiveFormsModule,
        SignatureFieldComponent_1,
        MatTooltip,
        MatButton,
        TranslateModule,
    ],
})
export class CheckoutModalDialogComponent implements OnInit {
    externalTokens: TokensExternalSignaturePad;
    externalSignatureUrl: string;
    readyToSave: boolean = false;

    signatureForm: UntypedFormGroup;
    organizationSiteKey: string;
    deliveryNoteBundleKey: string;

    driverSignatureSucceeded: boolean = false;
    driverSignatureImage: any;
    onClearSignature: boolean;

    signaturePad = viewChild(SignatureFieldComponent);

    constructor(
        private formBuilder: UntypedFormBuilder,
        private dialogRef: MatDialogRef<CheckoutModalDialogComponent>,
        private toastr: ToastService,
        private translateService: TranslateService,
        private deliveryNoteBundlesService: DeliveryNoteBundlesService,
        private signaturePadsService: SignaturePadsService,
        @Inject(MAT_DIALOG_DATA)
        public data: IncomingData,
        private spinner: NgxSpinnerService,
        private dialog: MatDialog,
        private errorHandler: ErrorHandler
    ) {}

    ngOnInit(): void {
        this.signatureForm = this.formBuilder.group({
            driverSignature: ['', signatureRequiredValidator()],
        });

        this.getTokensForExternalSignaturePad('checkout');
    }

    closeDialog(response: string) {
        this.dialogRef.close(response);
    }

    async saveFormData() {
        const base64Driver =
            this.signatureForm.controls['driverSignature'].value;
        // A checkout must never be submitted without a signature.
        if (!isValidSignatureDataUrl(base64Driver)) {
            this.readyToSave = false;
            this.toastr.error(
                this.translateService.instant(
                    'OUTGOING-GOODS.CHECKOUT.SIGNATURE-DIALOG.NO-SIGNATURE-FOUND'
                )
            );
            return;
        }
        this.spinner.show();
        const driverSignatureImage = base64Driver.toString().split(',')[1];
        const blobData = base64StringToBlob(driverSignatureImage, 'image/png');

        try {
            const result = await lastValueFrom(
                this.deliveryNoteBundlesService.postDeliveryNoteBundlesCheckout(
                    {
                        organizationSiteKey: this.data.organizationSiteKey,
                        body: {
                            deliveryNoteBundleKeys:
                                this.data.deliveryNoteBundleKeys,
                            driverSignature: driverSignatureImage,
                        },
                    }
                )
            );

            /*const triggerDate = moment(response.lastFeedback)
                .add(60, 'hours')
                .format();
            const now = moment().format();

            this.triggerFeedback = moment(triggerDate).isBefore(now);
            if (localStorage.getItem('hideRating') !== 'true') {
                this.rate().then(() => {
                    this.closeDialog(result.accessToken);
                });
            } else {
                this.closeDialog(result.accessToken);
            }*/

            this.toastr.success(
                this.translateService.instant(
                    'OUTGOING-GOODS.CHECKOUT.SIGNATURE-DIALOG.CONFIRM-PERSISTED'
                )
            );
        } catch (error) {
            this.errorHandler.handleError(error);
        } finally {
            this.spinner.hide();
        }
    }

    changeSignatureStatus(readyToSave: boolean): void {
        this.readyToSave = readyToSave;
    }

    /**
     * Receives tokens for processing data of external signature pad
     */
    async getTokensForExternalSignaturePad(type: string) {
        if (!this.data.deliveryNoteBundleKeys[0]) {
            return;
        }

        const response = await lastValueFrom(
            this.signaturePadsService.postConsignorSignaturePad({
                organizationSiteKey: this.data.organizationSiteKey,
                body: {
                    deliveryNoteBundleKeys: this.data.deliveryNoteBundleKeys,
                },
            })
        );

        if (response) {
            this.externalTokens = response;
            const host = window.location.protocol + '//' + window.location.host;

            this.externalSignatureUrl = `${host}${environment.externalSignatureUrl}?token=${response.carrierAccessToken}`;
        }
    }

    async pullSignature() {
        try {
            const response = await lastValueFrom(
                this.signaturePadsService.getConsignorSignatureFromSignaturePad(
                    {
                        organizationSiteKey: this.data.organizationSiteKey,
                        deliveryNoteBundleKey:
                            this.data.deliveryNoteBundleKeys[0],
                        token: this.externalTokens.getToken,
                    }
                )
            );

            const blobDataURL = await blobToDataURL(response);

            // Only accept a real, non-empty signature from the external pad.
            if (!isValidSignatureDataUrl(blobDataURL)) {
                this.changeSignatureStatus(false);
                this.toastr.info(
                    this.translateService.instant(
                        'OUTGOING-GOODS.CHECKOUT.SIGNATURE-DIALOG.NO-SIGNATURE-FOUND'
                    )
                );
                return;
            }

            this.signatureForm.get('driverSignature')?.patchValue(blobDataURL);
            this.driverSignatureImage = blobDataURL;
            this.changeSignatureStatus(true);
        } catch (error: any) {
            if (error.status === 404) {
                this.toastr.info(
                    this.translateService.instant(
                        'OUTGOING-GOODS.CHECKOUT.SIGNATURE-DIALOG.NO-SIGNATURE-FOUND'
                    )
                );
            } else {
                this.toastr.error(
                    this.translateService.instant(
                        'OUTGOING-GOODS.CHECKOUT.SIGNATURE-DIALOG.ERROR-PULLING-SIGNATURE'
                    )
                );
            }
        }
    }

    async rate() {
        const dialogRef = this.dialog.open(FeedbackComponent, {
            minWidth: '380px',
            disableClose: true,
        });
        await lastValueFrom(dialogRef.afterClosed());
        return 'done';
    }
}
